/* eslint-disable complexity */
import cs from 'classnames';
import Icon, { ICON_MAP } from '@mindtickle/icon';
import Tooltip from '@mindtickle/tooltip';
import { FormattedMessage } from 'react-intl';

import { getPopupContainerOnBody } from '@mindtickle/utils';

import messages from './messages';
import { StyledNavFooter, overlayStyle } from './styles';

import { useBerryHelpersEvent } from '@mindtickle/ui-shell-core/Berry';
import useFreshchatHelpers from '~/components/Freshchat/hooks/useFreshchatHelpers';
import { useState, useEffect } from 'react';

import EchoWidgetButton from './EchoWidgetButton';

const SupportChat = ({
  setWidgetLoaded,
  isCollapsed,
}: {
  setWidgetLoaded: (loaded: boolean) => void;
  isCollapsed: boolean;
}) => {
  const { toggleBerry, widgetOpen } = useBerryHelpersEvent();
  const { toggleFreshchatWidget } = useFreshchatHelpers();
  const [isFreshchatOpen, setIsFreshchatOpen] = useState(false);

  const [showButtons, setShowButtons] = useState({
    berry: false,
    freshchat: false,
  });

  const toggleFreshchat = () => {
    toggleFreshchatWidget();
    setIsFreshchatOpen(!isFreshchatOpen);
  };

  const handleShowChatBoxToggle = (event: MessageEvent) => {
    if (event.data?.type === 'showChatBoxToggle') {
      setShowButtons(prev => ({
        ...prev,
        ...event.data.payload,
      }));
      setWidgetLoaded(true);
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleShowChatBoxToggle);

    return () => {
      window.removeEventListener('message', handleShowChatBoxToggle);
    };
  }, []);

  const { berry: showBerryButton, freshchat: showFreshchatButton } = showButtons;

  useEffect(() => {
    const freshchatFrame = document.querySelector('.mt-fc_frame');
    if (freshchatFrame) {
      if (isCollapsed) {
        freshchatFrame.classList.remove('mt-sidebar-expanded');
      } else {
        freshchatFrame.classList.add('mt-sidebar-expanded');
      }
    }
  }, [isCollapsed, showFreshchatButton]);

  return (
    <>
      {showBerryButton && (
        <div data-testid="navigation-footer-copilot-btn" className="nav-action-btn-copilot-wrapper">
          {widgetOpen ? (
            <Icon onClick={toggleBerry} type={ICON_MAP.CROSS} className="nav-action-btn" />
          ) : (
            <Tooltip
              onClick={toggleBerry}
              title={<FormattedMessage {...messages.CHAT_WITH_SUPPORT} />}
              placement="right"
              getPopupContainer={getPopupContainerOnBody}
            >
              <Icon type={ICON_MAP.SPARKLE} className="nav-action-btn" />
            </Tooltip>
          )}
        </div>
      )}

      {showFreshchatButton && (
        <div
          data-testid="navigation-footer-freshchat-btn"
          className="nav-action-btn-copilot-wrapper"
        >
          {isFreshchatOpen ? (
            <Icon onClick={toggleFreshchat} type={ICON_MAP.CROSS} className="nav-action-btn" />
          ) : (
            <Icon onClick={toggleFreshchat} type={ICON_MAP.SPARKLE} className="nav-action-btn" />
          )}
        </div>
      )}
    </>
  );
};

// eslint-disable-next-line complexity
const NavFooter = ({
  toggleNavigation,
  autoCollapse,
  isCollapsed,
  isAdmin = true,
}: {
  toggleNavigation: () => void;
  autoCollapse: boolean;
  isCollapsed: boolean;
  isAdmin?: boolean;
}) => {
  const [widgetLoaded, setWidgetLoaded] = useState(false);

  return (
    <StyledNavFooter
      data-testid="navigation-footer"
      className={cs('navigation-footer', {
        'collapsed-state': isCollapsed,
        'expanded-state': !isCollapsed,
        'freshchat-berry-enabled': widgetLoaded,
      })}
    >
      {isAdmin && <SupportChat setWidgetLoaded={setWidgetLoaded} isCollapsed={isCollapsed} />}
      <EchoWidgetButton isCollapsed={isCollapsed} isAdmin={isAdmin} />
      {!autoCollapse && (
        <Tooltip
          key={isCollapsed ? 'COLLAPSE_SIDEBAR' : 'EXPAND_SIDEBAR'}
          title={
            isCollapsed ? (
              <FormattedMessage {...messages.EXPAND_SIDEBAR} />
            ) : (
              <FormattedMessage {...messages.COLLAPSE_SIDEBAR} />
            )
          }
          placement="right"
          getPopupContainer={getPopupContainerOnBody}
          overlayStyle={overlayStyle}
        >
          <div
            data-testid="icon-pin-sidebar"
            className="nav-container-action-btn-wrapper"
            onClick={toggleNavigation}
          >
            <Icon type={ICON_MAP.SLIDE_PREVIEW} className="toggle-icon-btn" />
          </div>
        </Tooltip>
      )}
    </StyledNavFooter>
  );
};

export default NavFooter;
